import React, { useEffect, useMemo, useState } from 'react'
import NavbarLayout from '../../../components/layout/NavbarLayout/NavbarLayout'
import './TrainingProgramsPage.scss'
import SoloCollapse from '../../../components/ui/SoloCollapse/SoloCollapse';
import {
    ColumnsType,
} from 'antd/lib/table/interface';
import TableObjects from '../../../components/ui/TableObjects/TableObjects';
import { useTableObject } from '../../../utils/hooks/useTableObject';
import { addCollapseIcon, editCollapseIcon } from '../../../components/ui/SoloCollapse/components/icons';
import TableActionButtons from '../../../components/ui/TableObjects/components/TableActionButtons/TableActionButtons';
import { useMutation, useQuery } from 'react-query';
import { EducationCourseObj, ObjDB, TrainingProgramsObj } from '../../../types/apiTypes';
import { api } from '../../../api/api';
import { getSelectedItems } from '../../../utils/parsers';
import { openNotification, tableLinkId } from '../../../utils/jsxUtils';
import { useParams } from 'react-router';
import { errorNotification, invalidateQueries, optionsQueryGetAll } from '../../../utils/rest';
import { SwitchTrainingProgramsItems } from '../../../types/propsTypes';
import { sortDataByFieldId } from '../../../utils/table';
import TrainingProgramsAddForm from './components/TrainingProgramsAddForm/TrainingProgramsAddForm';
import { SortableContainerProps } from 'react-sortable-hoc';
import arrayMove from 'array-move';
import { DraggableBodyRow, DraggableContainer, DragHandle } from '../../../components/table/dragTable/dragTable';
import TrainingProgramsEditForm from './components/TrainingProgramsEditForm/TrainingProgramsEditForm';
import { queryKeysEducationCourse, queryKeysTrainingPrograms } from '../../../static/data/queryKeys';
import useScrollMount from '../../../utils/hooks/useScrollMount';
import FieldHelper from '../../../components/common/FieldHelper/FieldHelper';
import { trainingProgramsHelpers } from './formData/trainingProgramsFieldHepler';
import FormDropDescription from '../../../components/table/TableDropDescription/TableDropDescription';
import { notification } from 'antd';


interface TrainingProgramsPageProps { }

type TableItem = ObjDB<TrainingProgramsObj>

const TrainingProgramsPage: React.FC<React.PropsWithChildren<TrainingProgramsPageProps>> = () => {

    const [openedAddForm, setOpenedAddForm] = useState<boolean>(false)
    const [openedEditForm, setOpenedEditForm] = useState<boolean>(false)
    const [editItem, setEditItem] = useState<TableItem | null>(null)
    const [tableHeadLoader, setTableHeadLoader] = useState<boolean>(false)
    const [allData, setAllData] = useState<TableItem[]>([])
    const [shouldShowSortNote, setShouldShowSortNote] = useState<boolean>(true)

    const params = useParams<{ educationCourseId: string }>()

    const {
        selectedRowKeys,
        setSelectedRowKeys,
        getColumnSearchProps,
    } = useTableObject()

    const queryEducationCourse = useQuery<ObjDB<EducationCourseObj>[], Error, ObjDB<EducationCourseObj>[]>(
        queryKeysEducationCourse[0],
        api.educationCourse.getAll,
        { ...optionsQueryGetAll }
    )

    const educationCourseById = queryEducationCourse.data?.find(c => c.id === +(params.educationCourseId || "0"))

    const deleteManyMutate = useMutation(
        api.trainingPrograms.deleteMany.bind(api.trainingPrograms),
        {
            mutationKey: 'training-program-delete-many',
            onSuccess() {
                invalidateQueries(queryKeysTrainingPrograms[1])
                openNotification(
                    'success',
                    'Выбранные объекты успешно удалены'
                )
                setSelectedRowKeys([])
            },
            onError: (err: Error) => errorNotification(err, 'Ошибка при удалении объектов'),
            onSettled() { setTableHeadLoader(false) },
            onMutate() { setTableHeadLoader(true) }
        }
    )

    const sortAllMutate = useMutation(
        api.trainingPrograms.sortMany.bind(api.trainingPrograms),
        {
            mutationKey: 'training-program-sort-all',
            onSuccess() {
                invalidateQueries(queryKeysTrainingPrograms[1])
                openNotification(
                    'success',
                    'Сортировка выполнена успешно'
                )
            },
            onError: (err: Error) => errorNotification(err, 'Ошибка при сортировке объектов'),
            onSettled() { setTableHeadLoader(false) },
            onMutate() { setTableHeadLoader(true) }
        }
    )

    const switchData: SwitchTrainingProgramsItems = { educationCourses: queryEducationCourse.data || [] }

    const queryAll = useQuery<undefined, Error, TableItem[]>(
        queryKeysTrainingPrograms[1],
        api.trainingPrograms.getAll,
        { ...optionsQueryGetAll }
    )

    const queryDelete = useMutation(
        'training-program-delete',
        api.trainingPrograms.delete,
        {
            onSuccess() {
                openNotification('success', 'Объект успешно удален')
                invalidateQueries(queryKeysTrainingPrograms[1])
            },
            onError: (err: Error) => errorNotification(err, 'Ошибка при удалении объекта')
        }
    )

    const columns: ColumnsType<TableItem> = useMemo(() => {
        return [
            {
                title: '№',
                dataIndex: 'sort',
                width: 30,
                className: 'drag-visible',
                render: () => <DragHandle />,
            },
            {
                title: 'ID',
                dataIndex: 'id',
                width: 70,
            },
            {
                title: 'Заголовок',
                dataIndex: 'title',
                width: 200,
            },
            {
                title: 'Количество часов',
                dataIndex: 'count_hours',
                width: 110,
            },
            {
                title: 'Индекс (сортировка)',
                dataIndex: 'number',
                render: (val: number) => val,
                width: 130,
            },
            {
                title: 'Курс обучения',
                dataIndex: 'education_course_id',
                render: (id) => tableLinkId(queryEducationCourse.data || [], id, 'educationCourses'),
                width: 190,
            },
            {
                title: 'Действие',
                dataIndex: '',
                align: 'right',
                render: (value: TableItem) => (
                    <TableActionButtons
                        onEdit={() => handleEditItem(value)}
                        onDelete={() => queryDelete.mutate(value)}
                    />
                ),
                width: 120,
            },
        ]
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getColumnSearchProps])

    const showSortNote = () => {
        if (shouldShowSortNote) {
            notification.open({
                message: 'Для сохранения последовательности элементов нажмите иконку "№" в заголовке таблицы',
                placement: 'bottomRight',
                onClose: () => setShouldShowSortNote(false)
            })
        }
    }

    const onSelectChange = (selectedRowKeys: React.Key[]) => {
        setSelectedRowKeys(selectedRowKeys)
    }

    const handleTableAdd = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' })
        setOpenedAddForm(true)
    }

    const handleEditItem = (value: TableItem) => {
        setEditItem(value)
        setOpenedEditForm(true)
        setOpenedAddForm(false)
        window.scrollTo({ top: 0, behavior: 'smooth' })
    }

    const handleTableDelete = async () => {
        const deleteItems = getSelectedItems<TableItem>(selectedRowKeys, allData)
        deleteManyMutate.mutate(deleteItems)
    }

    const handleTableRefresh = () => invalidateQueries(queryKeysTrainingPrograms)

    const handleSaveSort = async () => {
        sortAllMutate.mutate(allData)
    }

    const onSortEnd: SortableContainerProps['onSortEnd'] = ({ oldIndex, newIndex }) => {
        if (oldIndex !== newIndex) {
            showSortNote()
            const newData = arrayMove([...allData], oldIndex, newIndex).filter(el => !!el);
            setAllData(newData)
        }
    };

    useEffect(() => {
        const responseData = queryAll.data
        if (Array.isArray(responseData)) {
            setAllData(sortDataByFieldId(responseData, +(params.educationCourseId || 0), 'education_course_id'))
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queryAll.data])

    useScrollMount()

    return (
        <NavbarLayout>
            <section className="uif-training-program-page">
                <FieldHelper
                    content={trainingProgramsHelpers.pageTitle}
                    text={`Программы обучения для курса обучения "${educationCourseById?.name}"`}
                    isPageTitle
                />
                <div className="uif-training-program-page__collapses">
                    <SoloCollapse
                        header="Создать новый объект"
                        keyCollapse="add"
                        show={openedAddForm}
                        icon={addCollapseIcon}
                        setShow={setOpenedAddForm}
                    >
                        <TrainingProgramsAddForm
                            switchData={switchData}
                            setOpenedForm={setOpenedAddForm}
                        />
                    </SoloCollapse>
                    <SoloCollapse
                        header="Редактировать объект"
                        keyCollapse="edit"
                        show={openedEditForm}
                        icon={editCollapseIcon}
                        setShow={setOpenedEditForm}
                    >
                        <TrainingProgramsEditForm
                            editItem={editItem}
                            switchData={switchData}
                            setOpenedForm={setOpenedEditForm}
                            setEditItem={setEditItem}
                        />
                    </SoloCollapse>
                </div>

                <TableObjects
                    selectedRowKeys={selectedRowKeys}
                    count={allData.length}
                    columns={columns}
                    dataSource={allData}
                    rowSelection={{
                        selectedRowKeys: selectedRowKeys,
                        onChange: onSelectChange
                    }}
                    components={{
                        body: {
                            wrapper: DraggableContainer(onSortEnd),
                            row: DraggableBodyRow(allData),
                        },
                    }}
                    expandable={{
                        expandedRowRender: (record: TableItem) => (
                            <FormDropDescription
                                data={[
                                    { title: 'Описание программы', text: record.text },
                                ]}
                            />
                        ),
                    }}
                    onDelete={handleTableDelete}
                    onAdd={handleTableAdd}
                    onRefresh={handleTableRefresh}
                    onSaveSort={handleSaveSort}
                    rowKey="id"
                    isFetching={queryAll.isFetching}
                    headerLoader={tableHeadLoader}
                    scroll={{ x: 900 }}
                />
            </section>
        </NavbarLayout >
    )
}

export default TrainingProgramsPage